import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FaLock, FaExclamationCircle, FaArrowLeft } from "react-icons/fa";
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import PasswordService from '../../services/passwordService';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidCode, setIsValidCode] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    // Verify that we have a valid reset code
    if (!oobCode) {
      setIsValidCode(false);
      setError('Invalid or expired reset link. Please request a new one.');
    }
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      // Validate passwords match
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Validate password strength
      const passwordValidation = PasswordService.validatePassword(password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors.join('\n'));
        return;
      }

      // Confirm the password reset
      if (!oobCode) {
        throw new Error('Invalid reset code');
      }

      await confirmPasswordReset(auth, oobCode, password);
      toast.success('Password has been reset successfully!');
      navigate('/auth/signin');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError('Failed to reset password. Please try again.');
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isValidCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 sm:px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-truth">Invalid Reset Link</h1>
            <p className="mt-2 text-gray-400">This password reset link is invalid or has expired</p>
          </div>

          <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
            <CardContent className="space-y-6">
              <div className="bg-red-900/20 border border-red-800 rounded-md p-4 text-sm">
                <div className="flex items-start gap-2">
                  <FaExclamationCircle className="text-red-400 mt-1" />
                  <div>
                    <p>This password reset link is no longer valid.</p>
                    <p className="mt-2">Please request a new password reset link.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-4">
                <Link to="/auth/forgot-password">
                  <Button className="w-full">
                    Request New Reset Link
                  </Button>
                </Link>

                <Link
                  to="/auth/signin"
                  className="text-sm text-truth hover:underline flex items-center justify-center gap-2"
                >
                  <FaArrowLeft className="text-xs" />
                  Back to Sign In
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 sm:px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Reset Password</h1>
          <p className="mt-2 text-gray-400">Enter your new password</p>
        </div>

        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Set New Password</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Please enter a strong password that meets our security requirements
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="p-3 bg-red-900/30 border border-red-800 rounded-md flex items-center gap-2 text-sm">
                  <FaExclamationCircle className="text-red-500" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-800 rounded-md p-4 text-sm">
                <div className="flex items-start gap-2">
                  <FaExclamationCircle className="text-blue-400 mt-1" />
                  <div>
                    <p>Password must meet the following requirements:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>At least 12 characters long</li>
                      <li>Include uppercase and lowercase letters</li>
                      <li>Include numbers and special characters</li>
                      <li>Not contain common patterns</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <Link
                to="/auth/signin"
                className="text-sm text-truth hover:underline flex items-center justify-center gap-2"
              >
                <FaArrowLeft className="text-xs" />
                Back to Sign In
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword; 